// src/Views/Agentform.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Divider } from "@mui/material";
import DynamicAccordionList from "@/Components/Accordion/DynamicAccordionList";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import Agentjson from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentAir";
import { LuTrash2 } from "@/Components/Icons/Icons";
import CheckboxBox from "@/Components/Form/CheckboxBox";
import AgentSea from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentSea";
import AgentLand from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentLand";
import AgentCoLoad from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentCoLoad";
import AgentCurrencyUpLift from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentCurrencyUpLift";
import AgentBorrowAWS from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentBorrowAWS";
import AgentShippingLine from "@/Config/ImportJob/CreateNewCustomer/Agent/AgentShippingLine";
import { useGetShiplineMasterQuery } from "@/redux/Masters/Masters";

const Agentform = React.forwardRef(({ setFormData, formData }, ref) => {
  const [selectedShippingLine, setSelectedShippingLine] = useState(null);
  const [shippingList, setShippingList] = useState([]);
  const [isShippingAgentValue, setIsShippingAgentValue] = useState(false);
  const [originalShippingList, setOriginalShippingList] = useState([]);
  const [deletedShipmentIds, setDeletedShipmentIds] = useState([]); // [deletedShipmentIds]
  const [payLoadShippingList, setPayLoadShippingList] = useState([]);
  const airRef = useRef();
  const seaRef = useRef();
  const landRef = useRef();
  const coLoadRef = useRef();
  const currencyUpLiftRef = useRef();
  const borrowAwsRef = useRef();
  const shippingLineRef = useRef();
  const { data: shiplineData } = useGetShiplineMasterQuery();
  const hasAgent = formData?.agent && Object.keys(formData.agent).length > 0;

  const shippingData = hasAgent ? formData.agent.shippingList || [] : [];
  const isInitialized = useRef(false);
  const shippingHash = JSON.stringify(shippingData);


  useEffect(() => {
     if (!shippingData || !Array.isArray(shippingData)) return;

 if (isInitialized.current === shippingHash) return;
  isInitialized.current = shippingHash;

  
    // API success
    setOriginalShippingList(shippingData);
    setShippingList(shippingData);
    setIsShippingAgentValue(shippingData[0]?.isShippingAgent ?? false);
  }, [shippingData, formData]);
  const agentSections = [
    {
      title: "AIR",
      content: (
        <FormGenerator
          compKey={"agent.AIR"}
          key="Air"
          ref={airRef}
          schema={Agentjson}
        />
      ),
    },
    {
      title: "SEA",
      content: (
        <FormGenerator
          compKey={"agent.SEA"}
          Key="Sea"
          ref={seaRef}
          schema={AgentSea}
        />
      ),
    },
    {
      title: "Land",
      content: (
        <FormGenerator
          compKey={"agent.LAND"}
          ref={landRef}
          schema={AgentLand}
        />
      ),
    },
    {
      title: "Co-Load",
      content: (
        <FormGenerator
          compKey={"agent.coLoad"}
          ref={coLoadRef}
          schema={AgentCoLoad}
        />
      ),
    },
    {
      title: "Currency Uplift (CFX)",
      content: (
        <FormGenerator
          compKey={"agent.uplift"}
          ref={currencyUpLiftRef}
          schema={AgentCurrencyUpLift}
        />
      ),
    },
    {
      title: "Borrow AWBs",
      content: (
        <FormGenerator
          compKey={"agent.borrowAws"}
          ref={borrowAwsRef}
          schema={AgentBorrowAWS}
        />
      ),
    },
  ];
  // const [formData, setFormData] = useState({});

  React.useImperativeHandle(ref, () => ({
    // get current values from all accordion forms
    submit: () => {
      const AIR_values = airRef.current?.getCurrentValues?.() || {};
      const AIR_dirty = airRef.current?.getDirtyFields?.() || {};

      const SEA_values = seaRef.current?.getCurrentValues?.() || {};
      const SEA_dirty = seaRef.current?.getDirtyFields?.() || {};

      const LAND_values = landRef.current?.getCurrentValues?.() || {};
      const LAND_dirty = landRef.current?.getDirtyFields?.() || {};

      const coLoad_values = coLoadRef.current?.getCurrentValues?.() || {};
      const coLoad_dirty = coLoadRef.current?.getDirtyFields?.() || {};

      const borrowAws_values = borrowAwsRef.current?.getCurrentValues?.() || {};
      const borrowAws_dirty = borrowAwsRef.current?.getDirtyFields?.() || {};

      const uplift_values =
        currencyUpLiftRef.current?.getCurrentValues?.() || {};
      const uplift_dirty = currencyUpLiftRef.current?.getDirtyFields?.() || {};

      return {
        AIR: filterDirty(AIR_values, AIR_dirty),
        SEA: filterDirty(SEA_values, SEA_dirty),
        LAND: filterDirty(LAND_values, LAND_dirty),
        coLoad: filterDirty(coLoad_values, coLoad_dirty),
        borrowAws: filterDirty(borrowAws_values, borrowAws_dirty),
        currencyUpLift: filterDirty(uplift_values, uplift_dirty),
        payLoadShippingList,
        isShippingAgentValue,
        deletedShipmentIds,
      };
    },

    // submit all forms (if you wired submit() in FormGenerator)
    submitAll: async () => {
      const result = {};

      if (airRef.current?.submit) {
        result.AIR = await airRef.current.submit();
      }
      if (seaRef.current?.submit) {
        result.SEA = await seaRef.current.submit();
      }

      return result;
    },

    // reset all forms
    reset: () => {
      airRef.current?.reset?.();
      seaRef.current?.reset?.();
      landRef.current?.reset?.();
      coLoadRef.current?.reset?.();
      borrowAwsRef.current?.reset?.();
      currencyUpLiftRef.current?.reset?.();
      setDeletedShipmentIds([]);
      setPayLoadShippingList([]);
    },
  }));

  const filterDirty = (values, dirty) => {
    if (!values || !dirty) return {};

    const onlyDirty = {};

    Object.keys(dirty).forEach((key) => {
      if (dirty[key]) {
        onlyDirty[key] = values[key];
      }
    });

    return onlyDirty;
  };

  const handleAddShippingLine = (data) => {
    const selectedName =
      shippingLineRef.current?.getCurrentValues?.()?.shippingLine;
    const matchedShipline = shiplineData?.find((s) => s.id === selectedName);

    if (matchedShipline && !shippingList.includes(matchedShipline)) {
      setShippingList((prev) => [...prev, matchedShipline]);
      setPayLoadShippingList((prev) => [...prev, matchedShipline]);
      setSelectedShippingLine(null);
      shippingLineRef.current?.setValue?.("shippingLine", "");
      shippingLineRef.current?.enableButton();
    }
  };

  const handleRemoveShippingLine = (lineId) => {
    const item = originalShippingList.find((s) => s.id === lineId);
    if (item?.id) {
      setDeletedShipmentIds((prev) => [...prev, item.id]);
    }

    setShippingList((prev) => prev.filter((line) => line.id !== lineId));
    shippingLineRef.current?.enableButton();
  };

  // ⭐ remove duplicates by id
  const uniqueShippingList = shippingList.filter(
    (item, index, self) => index === self.findIndex((t) => t.id === item.id)
  );

  return (
    <Box
      display="flex"
      gap={2}
      width="100%"
      sx={{
        p: 2,
      }}
    >
      {/* Left Side */}
      <Box flex={1} px={0}>
        <Box sx={{}}>
          <DynamicAccordionList data={agentSections} />
        </Box>
      </Box>
      {/* Right Side */}
      <Box
        flex={1}
        sx={{
          border: `1px solid var(--gray-300)`,
          borderRadius: 4,
          height: "500px", // ⭐ FIXED HEIGHT
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.2,
            bg: "none",
          }}
        >
          <Typography variant="bodyBase">Shipping Agent</Typography>
          <Box display="flex" alignItems="center">
            <CheckboxBox
              value={isShippingAgentValue}
              onChange={(v) => {
                return setIsShippingAgentValue(v);
              }}
            />
            <Typography variant="bodySmall">Shipping Agent</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
          mx={4}
        >
          <FormGenerator
            compKey={"shippingLine"}
            schema={AgentShippingLine}
            onSubmit={handleAddShippingLine}
            ref={shippingLineRef}
          />
        </Box>

        {/* Table Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: "var(--brand-200)",
            padding: "12px",
            mb: 1,
          }}
        >
          <Typography variant="bodySmall" color="var(--gray-900)">
            Shipping Line
          </Typography>
          <Typography variant="bodySmall" color="var(--gray-900)">
            Actions
          </Typography>
        </Box>

        {/* List of Shipping Lines */}
        <Box
          sx={{
            maxHeight: "500px",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
          }}
        >
          {shippingList.map((line, index) => (
            <Box
              key={line.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                borderBottom: "1px solid var(--brand-80)",
                py: 1,
                px: 1.5,
              }}
            >
              <Typography variant="bodySmall">{line.name}</Typography>

              <Box
                sx={{ cursor: "pointer" }}
                onClick={() => handleRemoveShippingLine(line.id)}
              >
                <LuTrash2 style={{ fontSize: 20, color: "var(--brand-600)" }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
});

export default Agentform;
